import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertCircle, Building2, Loader2, Paperclip, Send, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRequestTypes, useCreateRequest } from "@/hooks/useInternalRequests";
import { uploadAttachment } from "@/api/attachments";
import { REQUEST_TYPE_LABEL, type CreateRequestPayload, type RequestType, type RequestTypeOption } from "@/api/internalRequests";

/**
 * 3B/#9 — «Запит у відділ» CANONICAL create drawer (D-031, ОДНА форма).
 *
 * Catalog-aware: when the backend serves the canonical route-driven catalog
 * (#9 flag ON ⇒ options carry `label`), the drawer adds: SLA-preview (calendar,
 * P8), dueDate auto from route SLA (source of truth — read-only) + «Бажаний
 * строк (опц.)», minimal type-specific fields (P4 — NOT a form-builder) and
 * attachments (P5 whitelist/10MB). With the legacy 3B catalog it renders
 * exactly the pre-#9 form. NULL=DENY: no route → submit blocked with reason.
 */
const LEGACY_ORDER: RequestType[] = [
  "reconciliation_statement", "payment_schedule", "contract_review_or_addendum",
  "pos_materials", "data_correction", "complaint_support", "other",
];

/** #9 P4 — minimal type-specific fields (mirror of the backend whitelist). */
const FORM_FIELDS: Record<string, { key: string; label: string; type: "text" | "date" | "textarea"; placeholder?: string }[]> = {
  reconciliation_statement: [
    { key: "periodFrom", label: "Період з", type: "date" },
    { key: "periodTo", label: "Період по", type: "date" },
  ],
  pos_materials: [
    { key: "materialType", label: "Тип матеріалу", type: "text", placeholder: "вивіска / стенд / POS…" },
    { key: "brand", label: "Бренд", type: "text" },
    { key: "city", label: "Місто", type: "text" },
  ],
  promo: [
    { key: "brand", label: "Бренд", type: "text" },
    { key: "campaignNote", label: "Суть акції", type: "textarea" },
  ],
  contract_review: [{ key: "changeDescription", label: "Що перевірити / змінити", type: "textarea" }],
  contract_addendum: [{ key: "changeDescription", label: "Суть змін (допугода)", type: "textarea" }],
  data_correction: [
    { key: "field", label: "Поле / реквізит", type: "text" },
    { key: "currentValue", label: "Поточне значення", type: "text" },
    { key: "newValue", label: "Нове значення", type: "text" },
  ],
};

function plusDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

interface PendingFile { fileId: string; name: string; size: number }

/**
 * D-055 S3 — staged-mode (P7): дровер ПОВЕРТАЄ spec замість POST. Реальний
 * запит створюється server-side на submit звіту (одна транзакція, #9 service,
 * source.module=meeting_report). ОДНА форма на CRM — staged = prop.
 */
export interface StagedRequestSpec {
  kind: "internal_request";
  requestType: string;
  requestTypeLabel: string;
  departmentName?: string;
  subject: string;
  body?: string;
  departmentId?: string;
  formData?: Record<string, string>;
}

export function CreateInternalRequestDrawer({
  open, onClose, holdingId, holdingName, sourceModule, sourceEntityId, contractId, complaintId, staged,
  initialRequestType, initialBody,
}: {
  open: boolean;
  onClose: () => void;
  holdingId?: string;
  holdingName?: string;
  /** #9 provenance (audit): client360 / home / task / contract / complaint / profile. */
  sourceModule?: string;
  sourceEntityId?: string;
  contractId?: string;
  complaintId?: string;
  /** D-055 S3: staged-mode — onStage(spec) замість POST (звіт зустрічі). */
  staged?: { onStage: (spec: StagedRequestSpec) => void } | null;
  /** G1-S3: open pre-set to a type (e.g. contract_terms_change) + prefilled body. */
  initialRequestType?: RequestType;
  initialBody?: string;
}) {
  const headingId = useId();
  const panelRef = useDrawerA11y(open, onClose);
  const typesQ = useRequestTypes(open);
  const mutation = useCreateRequest();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // canonical catalog ⇒ options carry backend-owned labels (#9 flag ON).
  const canonical = !!typesQ.data?.types.some((t) => t.label !== undefined);
  const options: RequestTypeOption[] = typesQ.data?.types ?? [];
  const orderedTypes: RequestTypeOption[] = canonical
    ? options
    : LEGACY_ORDER.map((t) => options.find((o) => o.type === t)).filter((o): o is RequestTypeOption => !!o);
  const otherKey = canonical ? "custom_request" : "other";

  const [requestType, setRequestType] = useState<RequestType>("reconciliation_statement");
  const [departmentId, setDepartmentId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [desiredDueDate, setDesiredDueDate] = useState("");
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const typeOpt = useMemo(
    () => options.find((t) => t.type === requestType) ?? null,
    [options, requestType],
  );

  useEffect(() => {
    if (!open) return;
    setRequestType(initialRequestType ?? "reconciliation_statement"); setDepartmentId("");
    setSubject(""); setBody(initialBody ?? "");
    setDueDate(""); setDesiredDueDate(""); setFormData({}); setFiles([]); setErr(null);
  }, [open, initialRequestType, initialBody]);

  // auto due-date: route SLA (canonical, source of truth) / defaultDueDays (legacy).
  useEffect(() => {
    if (!open || !typeOpt) return;
    setDueDate(typeOpt.defaultDueDays != null ? plusDays(typeOpt.defaultDueDays) : "");
    setFormData({});
  }, [open, typeOpt]);

  if (!open) return null;

  const isStaged = !!staged;
  const isOther = requestType === otherKey;
  const routeAvailable = !!typeOpt?.available;
  const typeLabel = (t: RequestTypeOption) =>
    t.label ?? REQUEST_TYPE_LABEL[t.type as RequestType] ?? t.type;
  const dynFields = canonical ? FORM_FIELDS[requestType] ?? [] : [];
  const canSubmit =
    subject.trim() !== "" &&
    routeAvailable &&
    (!isOther || departmentId !== "") &&
    (isStaged || dueDate !== "") &&
    !mutation.isPending && !uploading;

  const pickFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true); setErr(null);
    try {
      for (const f of Array.from(list).slice(0, 10 - files.length)) {
        if (f.size > 10 * 1024 * 1024) { setErr(`«${f.name}» понад 10MB`); continue; }
        const up = await uploadAttachment(f, "CInternalRequestAttachment", "file");
        setFiles((prev) => [...prev, { fileId: up.id, name: f.name, size: f.size }]);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося завантажити файл");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async () => {
    if (subject.trim() === "") { setErr("Вкажіть тему"); return; }
    if (isOther && !departmentId) { setErr("Оберіть відділ для «Інше»"); return; }

    // D-055 S3 staged: жодного POST — spec у Actions Builder; створення
    // server-side на submit звіту (route-availability вже гейтнута вище).
    if (staged) {
      staged.onStage({
        kind: "internal_request",
        requestType,
        requestTypeLabel: typeOpt ? typeLabel(typeOpt) : requestType,
        departmentName: typeOpt?.departmentName ?? undefined,
        subject: subject.trim(),
        body: body.trim() || undefined,
        departmentId: isOther ? departmentId : undefined,
        formData: Object.keys(formData).length > 0 ? formData : undefined,
      });
      onClose();
      return;
    }

    if (!dueDate) { setErr("Вкажіть дедлайн"); return; }
    const payload: CreateRequestPayload = {
      requestType, subject: subject.trim(), body: body.trim() || undefined, dueDate,
    };
    if (isOther) payload.departmentId = departmentId;
    if (holdingId) payload.holdingId = holdingId;
    if (canonical) {
      if (desiredDueDate) payload.desiredDueDate = desiredDueDate;
      if (Object.keys(formData).length > 0) payload.formData = formData;
      if (files.length > 0) payload.attachmentFileIds = files.map((f) => f.fileId);
      payload.sourceModule = sourceModule ?? "requests_page";
      if (sourceEntityId) payload.sourceEntityId = sourceEntityId;
      if (contractId) payload.contractId = contractId;
      if (complaintId) payload.complaintId = complaintId;
    }
    try {
      await mutation.mutateAsync(payload);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося надіслати запит");
    }
  };

  const fieldCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
  const lblCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={headingId} tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-y-auto bg-background shadow-2xl outline-none sm:w-[450px]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <Send className="h-4 w-4 text-primary" /> Запит у відділ
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-4 px-5 py-5">
          {isStaged && (
            <p className="rounded-md border border-violet-foreground/20 bg-violet px-3 py-2 text-[11.5px] text-violet-foreground">
              Чернетка дії — запит створиться <b>після подання звіту</b> (одна транзакція). Дедлайн — авто за SLA маршруту.
            </p>
          )}
          {/* requestType */}
          <div>
            <label className={lblCls}>Тип запиту</label>
            <select value={requestType} onChange={(e) => setRequestType(e.target.value as RequestType)} className={fieldCls}>
              {orderedTypes.map((t) => <option key={t.type} value={t.type}>{typeLabel(t)}</option>)}
            </select>
            {/* route preview */}
            {!isOther && typeOpt && (
              routeAvailable ? (
                <div className="mt-2 rounded-md border border-violet-foreground/20 bg-violet px-3 py-2 text-[12.5px] text-violet-foreground">
                  → Піде у відділ: <b>{typeOpt.departmentName}</b> · відповідальні отримають сповіщення
                  {canonical && typeOpt.claimSlaHours != null && (
                    <div className="mt-0.5 text-[11.5px] opacity-80">
                      SLA: взяти {typeOpt.claimSlaHours} год · виконати {typeOpt.completeSlaHours} год — рахується календарно
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-1.5 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[12px] text-warning-foreground">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {typeOpt.reason ?? "Маршрут не налаштований."}
                </div>
              )
            )}
          </div>

          {/* department picker for «Інше» */}
          {isOther && (
            <div className="rounded-lg border border-dashed border-warning-foreground/40 bg-warning px-3 py-2.5">
              <label className={lblCls}>Підрозділ <span className="text-destructive">*</span> <span className="font-normal normal-case text-muted-foreground">— для «Інше»</span></label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className={fieldCls}>
                <option value="">— оберіть відділ —</option>
                {typesQ.data?.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-muted-foreground">Нестандартний запит теж іде в ЧЕРГУ відділу (не напряму людині). Тема/опис/дедлайн обовʼязкові.</p>
            </div>
          )}

          {/* client context (read-only when opened from Client360) */}
          {holdingName && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" /> {holdingName}
            </div>
          )}

          <div>
            <label className={lblCls}>Тема <span className="text-destructive">*</span></label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={fieldCls} placeholder="напр. Акт звірки за травень" autoFocus />
          </div>

          {/* #9 P4 — minimal type-specific fields (canonical catalog only) */}
          {dynFields.length > 0 && (
            <div className="space-y-3 rounded-lg border bg-muted/20 px-3 py-3">
              {dynFields.map((f) => (
                <div key={f.key}>
                  <label className={lblCls}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={2} value={formData[f.key] ?? ""} className={fieldCls} placeholder={f.placeholder}
                      onChange={(e) => setFormData((p) => ({ ...p, [f.key]: e.target.value }))} />
                  ) : (
                    <input type={f.type} value={formData[f.key] ?? ""} className={fieldCls} placeholder={f.placeholder}
                      onChange={(e) => setFormData((p) => ({ ...p, [f.key]: e.target.value }))} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div>
            <label className={lblCls}>Опис</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className={fieldCls} placeholder="Деталі запиту…" />
          </div>

          {!isStaged && (canonical ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lblCls}>Буде виконано до</label>
                <input type="date" value={dueDate} readOnly className={cn(fieldCls, "bg-muted/40 text-muted-foreground")} />
                <p className="mt-1 text-[11px] text-muted-foreground">Авто за SLA маршруту (календарно).</p>
              </div>
              <div>
                <label className={lblCls}>Бажаний строк <span className="font-normal normal-case">(опц.)</span></label>
                <input type="date" value={desiredDueDate} onChange={(e) => setDesiredDueDate(e.target.value)} className={fieldCls} />
                <p className="mt-1 text-[11px] text-muted-foreground">Не перекриває SLA; відділ бачить обидва.</p>
              </div>
            </div>
          ) : (
            <div>
              <label className={lblCls}>Дедлайн <span className="text-destructive">*</span></label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={fieldCls} />
              {typeOpt?.defaultDueDays != null && (
                <p className="mt-1 text-[11px] text-muted-foreground">Авто з типу запиту (defaultDueDays = {typeOpt.defaultDueDays}). Можна змінити.</p>
              )}
            </div>
          ))}

          {/* #9 P5 — attachments (canonical catalog only; staged: після створення запиту) */}
          {canonical && !isStaged && (
            <div>
              <label className={lblCls}>Вкладення <span className="font-normal normal-case">(до 10 файлів, ≤10MB: pdf/jpg/png/xlsx/docx)</span></label>
              <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx,.doc,.xls"
                onChange={(e) => void pickFiles(e.target.files)} />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || files.length >= 10}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                Додати файл
              </button>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((f) => (
                    <li key={f.fileId} className="flex items-center justify-between rounded-md border bg-muted/30 px-2.5 py-1.5 text-[12.5px]">
                      <span className="truncate">{f.name} <span className="text-muted-foreground">({Math.round(f.size / 1024)} KB)</span></span>
                      <button type="button" aria-label={`Прибрати ${f.name}`} className="ml-2 text-muted-foreground hover:text-destructive"
                        onClick={() => setFiles((prev) => prev.filter((x) => x.fileId !== f.fileId))}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-[11px] text-muted-foreground">Видимі: автор + відділ + admin.</p>
            </div>
          )}

          {err && <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" /> {err}</p>}
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t bg-background px-5 py-4">
          <button type="button" onClick={submit} disabled={!canSubmit}
            className={cn("inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90", !canSubmit && "cursor-not-allowed opacity-50")}>
            {mutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Надсилання…</> : isStaged ? "Додати до звіту" : "Надіслати у відділ"}
          </button>
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground">Скасувати</button>
        </div>
      </div>
    </div>
  );
}
